import { DEFAULT_APP_SETTINGS, DEFAULT_SYSTEM_SETTINGS } from '../constants/settings';

/** Every `settings/system` value, each missing or invalid one (or all, when the doc is missing) defaulted and reported through `capture`. */
export const resolveSystemSettings = (
  stored: Partial<Record<keyof typeof DEFAULT_SYSTEM_SETTINGS, unknown>> | undefined,
  capture: (message: string, level: 'warning') => unknown
) => resolveSettings('settings/system', stored, DEFAULT_SYSTEM_SETTINGS, SYSTEM_SETTINGS_RANGES, capture);

/** Every `settings/app` value, each missing or invalid one (or all, when the doc is missing) defaulted and reported through `capture`. */
export const resolveAppSettings = (
  stored: Partial<Record<keyof typeof DEFAULT_APP_SETTINGS, unknown>> | undefined,
  capture: (message: string, level: 'warning') => unknown
) => resolveSettings('settings/app', stored, DEFAULT_APP_SETTINGS, APP_SETTINGS_RANGES, capture);

export const SYSTEM_SETTINGS_RANGES: Record<keyof typeof DEFAULT_SYSTEM_SETTINGS, readonly [min: number, max: number]> = {
  aiPreviewCredits: [1, Infinity],
  trialDays: [1, 60],
  trialCredits: [1, Infinity],
  gracePeriodQuotaPercent: [0, 99],
};

export const APP_SETTINGS_RANGES: Record<keyof typeof DEFAULT_APP_SETTINGS, readonly [min: number, max: number]> = {
  quotaPercentUsedWarning: [0, 100],
  creditsPerTypicalNote: [1, 1000],
};

/** Whether `value` is an integer within `[min, max]`, the one rule readers and the admin share. */
export const isValidSettingValue = (value: unknown, [min, max]: readonly [min: number, max: number]): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;

/** Every key of `defaults`, taken from `stored` when it holds an integer within its range, else the default, reported through `capture`. */
function resolveSettings<T extends Record<string, number>>(
  docPath: string,
  stored: Partial<Record<keyof T, unknown>> | undefined,
  defaults: T,
  ranges: Record<keyof T, readonly [min: number, max: number]>,
  capture: (message: string, level: 'warning') => unknown
): T {
  const resolved: Record<string, number> = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof T & string)[]) {
    const value = stored?.[key];
    if (isValidSettingValue(value, ranges[key])) {
      resolved[key] = value;
    } else {
      capture(
        `[settings] ${docPath}.${key} missing or invalid (${formatValue(value)}); using default ${defaults[key]}`,
        'warning'
      );
    }
  }
  return resolved as T;
}

const formatValue = (value: unknown) => (typeof value === 'string' ? JSON.stringify(value) : String(value));
